<template>
  <div
    class="wishcard-big"
    :class="wishcardClasses"
  >
    <div
      class="wishcard-big__preview"
    >
      <div
        class="wishcard-preview"
      >
        <div class="thumbnail">
          <img
            v-if="siteThumbnail && card.size.toLowerCase() === 'big'"
            class="wishcard-preview__img"
            :src="siteThumbnail"
            :alt="$t('label.wishcard')"
          />
          <img
            v-else-if="hasThumbnailImage && card.size.toLowerCase() === 'funeral'"
            class="wishcard-preview__img"
            :src="card.thumbnail"
            :alt="`${$t('label.wishcard')}: ${card.name}`"
          />
        </div>
      </div>
    </div>

    <div class="wishcard-big__inner-right">
      <h4
        class="wishcard-big__label"
        v-text="`${$t('wishcard.inner_side.right')}:`"
      />
      <tg-wishcard-textarea
        v-bind="{...$props, ...wishcardProps}"
        @validate="onValidate"
        :show-layered-image="true"
        :show-default-placeholder="false"
      />
    </div>
  </div>
</template>

<script>
import TgWishcardTextarea from './TgWishcardTextarea';

export default {
  name: 'TgWishcardBig',

  components: {
    TgWishcardTextarea
  },

  data() {
    return {
      hasErrors: false
    }
  },

  props: {
    cardText: {
      type: String,
      required: true,
      default: ''
    },
    card: {
      type: [Object, null],
      required: true,
      default: null
    },
    siteThumbnail: {
      // type: [String, null], // Gives issues at the moment.
      required: true,
      default: ''
    }
  },

  computed: {
    wishcardProps() {
      if (!this.card || this.card.size === 'big') {
        return {
          maxLines: 10,
          maxLength: 400,
          fontSizeRatio: 0.038,
          lineHeightRatio: 0.060
        }
      }

      // Used for funeral cards
      return {
        maxLines: 9,
        maxLength: 460,
        fontSizeRatio: 0.035,
        lineHeightRatio: 0.069
      }
    },
    hasImage() {
      return this.card && this.card.image
    },
    hasBgImage() {
      return this.card && this.card.bgImage
    },
    hasThumbnailImage() {
      return this.card && this.card.thumbnail
    },
    wishcardClasses() {
      const classes = [];
      if (this.hasImage) {
        classes.push('wishcard-big--show-preview');
      }

      if (this.hasErrors) {
        classes.push('wishcard-big--has-error');
      }

      if (this.card && this.card.isHandwritten) {
        classes.push('wishcard-big--handwritten');
      }

      if (this.hasBgImage) {
        classes.push('wishcard-big--texture');
      }

      if (this.card && this.card.size) {
        classes.push(`wishcard-big--${this.card.size}`);
      }

      return classes;
    }
  },

  methods: {
    onValidate({tooManyLines, tooManyCharacters}) {
      this.hasErrors = tooManyLines === true || tooManyCharacters === true;
    }
  }
}
</script>

<style scoped>
.wishcard-big__label {
  font-size: .75rem;
  font-weight: normal;
  font-style: italic;
  color: var(--text-color);
}

.wishcard-big__inner-right {
  position: relative;
}

.wishcard-big >>> .wishcard__field {
  position: relative;
  z-index: 5;
  border-radius: 1rem;
  overflow: hidden;
  background-size: cover;
  background-repeat: no-repeat;
  aspect-ratio: 1.409; /* A6 Format */
}

.wishcard-big >>> .wishcard__field:before {
  border-radius: 1rem;
  content: '';
  display: block;
  top: 0;
  left: 0;
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  transition: all 300ms ease-in;
  opacity: 1;
}

.wishcard-big >>> .wishcard__field:before {
  border: 2px var(--light-grey-22) solid;
}

.wishcard-big--texture >>> .wishcard__field:before {
  opacity: .25;
}

.wishcard-big--has-error >>> .wishcard__field:before {
  border: 2px var(--danger) solid;
  opacity: 1;
}

.wishcard-big >>> .wishcard__input {
  padding: 5% 22% 5% 5%;
}

.wishcard-big >>> .wishcard__placeholder {
  padding: 5% 22% 0 5% !important;
}

.wishcard-big--funeral >>> .wishcard__input {
  padding: 5%;
}

.wishcard-big--funeral >>> .wishcard__placeholder {
  padding: 5% 5% 0 5% !important;
}

.wishcard-big__preview {
  display: flex;
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: auto;
  padding: 0 0 1rem;
}

.wishcard-big--big .wishcard-big__preview {
  display: none;
  padding: 0;
}

.wishcard-big--funeral .wishcard-big__preview {
  justify-content: center;
}

.wishcard-big--funeral .wishcard-preview {
  width: 13.625rem;
}

.wishcard-big--big .thumbnail {
  text-align: right;
}

.wishcard-big--funeral .thumbnail {
  position: relative;
  width: 100%;
  aspect-ratio: 1.47;
}

.wishcard-big--funeral .thumbnail:before {
  display: none;
  top: -9px;
  left: 0;
  position: absolute;
  content: "";
  background-color: var(--light-grey-22);
  width: 90%;
  height: 100%;
  transform: skew(0deg, -5deg);
}

.wishcard-big--funeral .wishcard-preview__img {
  position: relative;
  z-index: 10;
}

.wishcard-big--funeral .thumbnail:after {
  display: none;
  width: 3.1875rem;
  height: .75rem;
  position: absolute;
  top: -2rem;
  content: "";
  background-repeat: no-repeat;
  background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTEiIGhlaWdodD0iMTIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTUwLjU3OSAxMC40ODRjLTcuNDM1LTYuOC0yNy43NzUtMTYuMzE5LTQ5LjY1IDBtMCAwTDQgM00uOTMgMTAuNDg0TDkgOS41IiBzdHJva2U9IiMwMDAiLz48L3N2Zz4=);
}


@media (min-width: 75rem) {
  .wishcard-big {
    display: flex;
  }

  .wishcard-big__preview {
    padding: 2rem 0;
  }

  .wishcard-big__label {
    display: none;
  }

  .wishcard-big--funeral .thumbnail:after {
    display: block;
    right: -2.5rem;
  }

  .wishcard-big--funeral .thumbnail:before {
    display: block;
  }

  .wishcard-big--big .wishcard-big__preview {
    display: block;
    padding-top: 28%;
    justify-content: flex-end;
  }

  .wishcard-big--funeral .wishcard-big__preview {
    padding-top: 8%;
  }

  .wishcard-big__inner-right {
    width: 50%;
  }
}

@media (min-width: 87.5rem) {
  .wishcard-big--funeral .thumbnail:after {
    right: -3.5rem;
  }
}
</style>
